import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { GitMerge, RefreshCw, MoreVertical } from "react-feather";
import {
  Loader,
  AppError,
  EmptyState,
  Table,
  TableHead,
  TableHeaderCell,
  Menu,
  MenuListItem,
  TableBody,
  Checkbox,
  TableRow,
  TableCell,
  Tag,
  Tooltip,
  Select,
  selectState as ss,
  toast,
  type SelectMsg,
  type SelectState,
  type TagTone,
} from "@bfc-software/pallet";
import { match } from "ts-pattern";
import { useContext, useEffect, useState } from "react";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import {
  AuthContext,
  AuthUtils,
  logger,
  useAssertedTenancyContext,
} from "@bfc-software/app-toolbox";
import { useTaskPlannerApi } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import {
  type PageSearch,
  pageQuery,
  validatePageSearch,
} from "@bfc-software/bfc-web-ui/lib/cursor-pagination";
import { ActivationFilter } from "@bfc-software/bfc-web-ui/lib/task-plan-activation";
import { taskPlanName } from "@bfc-software/bfc-web-ui/lib/task-utils/naming";
import { ListPager } from "@bfc-software/bfc-web-ui/ui/shared/list-pager";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";
import { UseQueryResult } from "@tanstack/react-query";
import { isDefined } from "@bfc-software/app-toolbox";

import {
  type PlanActivationState,
  type TaskPlan,
  type TaskPlanListResponse,
  PPT_PLAN_OPTIMIZATION_KIND,
} from "./-components/types";
import { customerNamesOf, zoneLabelsOf } from "./-data/labels";
import sharedStyles from "./-components/shared.module.css";
import styles from "./task-plans.module.css";

type State =
  | { kind: "not-loaded" }
  | { kind: "refresh-started" }
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "empty" }
  | {
      kind: "loaded";
      plans: TaskPlan[];
      selectedPlans: Array<TaskPlan>;
      dataUpdatedAt: number;
    }
  | {
      kind: "optimizing";
      targets: Array<TaskPlan>;
    };

/**
 * The two members of `ActivationFilter` this page offers, in Select order.
 *
 * A subset, not the whole enum: the vocabulary is shared with the Outbound plans
 * tab (`lib/task-plan-activation`, and it has to be - the router registers one
 * `activation` param for the whole app) but this page is a plan *inventory*
 * rather than a release queue. It asks whether a plan was ever released, not what
 * the floor has done with it since - so it offers `ACTIVATED`, the whole release,
 * and leaves the three `fulfillmentState` members to the tab that acts on them.
 * Anything outside this list, including a member only the other screen offers,
 * degrades to unfiltered below.
 *
 * Absent means unfiltered here, which is not what it means on the Outbound tab -
 * each screen's default is its own.
 */
const ACTIVATION_FILTER_OPTIONS: ReadonlyArray<ActivationFilter> = [
  ActivationFilter.ACTIVATED,
  ActivationFilter.NOT_ACTIVATED,
];

/**
 * Doubles as the Select's `Key` narrowing: anything a hand-edited URL carries
 * comes back as `undefined`, which is unfiltered.
 */
function validateActivationFilter(raw: unknown): ActivationFilter | undefined {
  return ACTIVATION_FILTER_OPTIONS.includes(raw as ActivationFilter)
    ? (raw as ActivationFilter)
    : undefined;
}

/**
 * Spread into the list request's `query`.
 *
 * The endpoint filters on the plan's CURRENT activation status, not on a
 * boolean: `none` selects plans that have never been activated, and every other
 * value selects plans whose most recent activation has that status. `completed`
 * is the only one the UI calls activated -- the status column reads a queued or
 * processing activation as "activating" and a failed one as "activation
 * failed", so neither sits on either side of this filter and both are reachable
 * only from the unfiltered view.
 */
function activationQuery(filter: ActivationFilter | undefined): {
  activationState?: "none" | "completed";
} {
  switch (filter) {
    case ActivationFilter.ACTIVATED:
      return { activationState: "completed" };
    case ActivationFilter.NOT_ACTIVATED:
      return { activationState: "none" };
    default:
      return {};
  }
}

type TaskPlanSearch = { activation?: ActivationFilter } & PageSearch;

/**
 * Rows per request.
 *
 * Held here rather than shared, because the server derives `page_number` and
 * `page_count` from the limit of the request that produced them -- so the limit
 * is part of what a page ordinal means, and a route that changes it renumbers
 * its own pages and nobody else's. Not user-adjustable: page size in the URL
 * would renumber pages under the reader mid-walk.
 */
const PAGE_SIZE = 25;

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/task-plans/",
)({
  validateSearch: (search?: Record<string, unknown>): TaskPlanSearch => {
    const activation = validateActivationFilter(search?.["activation"]);
    return {
      ...(isDefined(activation) ? { activation } : {}),
      ...validatePageSearch(search),
    };
  },
  staticData: {
    crumb: (_: unknown, t: TFunction) => {
      return { label: t("page.taskPlans.crumb") };
    },
  },
  component: RouteComponent,
});

const STATUS_TONES: Record<TaskPlan["status"], TagTone> = {
  completed: "success",
  processing: "info",
  queued: "neutral",
  failed: "danger",
};

/**
 * An activation's own job status, which shares the plan status vocabulary but
 * not its meaning: a `completed` activation is a plan that is out on the floor,
 * not a plan that finished generating.
 */
const ACTIVATION_TONES: Record<PlanActivationState["status"], TagTone> = {
  completed: "success",
  processing: "info",
  queued: "neutral",
  failed: "danger",
};

/**
 * The status column speaks about the activation once there is one.
 *
 * A plan only becomes activatable after it has finished generating, so an
 * activated row's `plan.status` is always `completed` -- repeating it next to
 * the activation would be two tags saying the same word for different reasons.
 */
function PlanStatusCell({ plan, t }: { plan: TaskPlan; t: TFunction }) {
  const { activation } = plan;

  // Tag stamps its own data-test-role, so the row-level hook goes on a wrapper.
  if (!isDefined(activation)) {
    return (
      <span data-test-role="plan-status">
        <Tag
          label={plan.status}
          display={{ tone: STATUS_TONES[plan.status] }}
        />
      </span>
    );
  }

  return (
    <span data-test-role="plan-activation-status">
      <Tag
        label={t(`page.taskPlans.activation.${activation.status}`)}
        display={{ tone: ACTIVATION_TONES[activation.status] }}
      />
    </span>
  );
}

/**
 * Pull the human-readable half out of the task planner's ErrorResponse
 * envelope, which is what a failed mutation rejects with -- it is a plain
 * object, not an Error, so `.message` is not reachable by the usual route.
 */
function errorMessage(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "object" && error !== null && "message" in error) {
    const { message } = error as { message: unknown };
    return typeof message === "string" ? message : undefined;
  }
  return undefined;
}

function countTasksWithOrphans(plan: TaskPlan): number {
  return (plan.optimization_metrics ?? []).reduce((acc, metric) => {
    switch (metric.kind) {
      case PPT_PLAN_OPTIMIZATION_KIND: {
        const realized = metric.ppt?.realized_pallets_per_task ?? 0;
        const effective = metric.ppt?.effective_pallets_per_task ?? 0;
        return effective > realized ? acc + 1 : acc;
      }
      default:
        return acc;
    }
  }, 0);
}

/**
 * Right-most per-row treatment for activating a task plan.
 *
 * Three states, driven by `activation` (null until the plan has ever been
 * activated) and plan `status`:
 *  - already activated -> reset the activation state, or view the plan detail
 *  - completed, not yet activated -> dropdown that fires the activation POST
 *  - not completed -> a disabled kebab with a tooltip (activation would 409)
 */
function PlanActionsCell({
  plan,
  t,
  onActivate,
  isActivating,
  onResetActivation,
  isResetting,
}: {
  plan: TaskPlan;
  t: TFunction;
  onActivate: (planId: string, selectorIds?: string[]) => void;
  isActivating: boolean;
  onResetActivation: (planId: string) => void;
  isResetting: boolean;
}) {
  const { org, location, lang } = Route.useParams();
  const navigate = useNavigate();

  const menuTrigger = {
    kind: "icon-only" as const,
    variant: "tertiary" as const,
    icon: "MoreVertical" as const,
    label: t("page.taskPlans.rowActions.menuAriaLabel"),
  };

  if (isDefined(plan.activation)) {
    return (
      <div className={styles.taskPlanRowActionsContainer}>
        <Menu trigger={menuTrigger}>
          {/*
            `in_flight` is the exact predicate the endpoint 409s on (ADR-0019
            §3.1.3), so we disable rather than let the call fail. A queued or
            processing activation is deliberately not resettable: the worker
            cannot cope with its state disappearing mid-run.
          */}
          <MenuListItem
            data-test-role="plan-reset-activation"
            isDisabled={isResetting || plan.activation.in_flight}
            onAction={() => onResetActivation(plan.id)}
          >
            {t("page.taskPlans.rowActions.resetActivation")}
          </MenuListItem>

          <MenuListItem
            data-test-role="plan-view-details"
            onAction={() =>
              navigate({
                to: "/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan",
                params: { "task-plan": plan.id, org, location, lang },
              })
            }
          >
            {t("page.taskPlans.rowActions.viewDetails")}
          </MenuListItem>
        </Menu>
      </div>
    );
  }

  if (plan.status !== "completed") {
    return (
      <div className={styles.taskPlanRowActionsContainer}>
        <Tooltip
          content={t("page.taskPlans.rowActions.activateDisabledTooltip")}
        >
          <span
            className={styles.taskPlanRowActionsDisabled}
            data-test-role="plan-activate-disabled"
          >
            <MoreVertical size={18} />
          </span>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={styles.taskPlanRowActionsContainer}>
      <Menu trigger={menuTrigger}>
        <MenuListItem
          isDisabled={isActivating}
          onAction={() => onActivate(plan.id)}
          data-test-role="plan-activate-all-selectors"
        >
          {t("page.taskPlans.rowActions.activateAllSelectors")}
        </MenuListItem>
        <MenuListItem
          isDisabled={isActivating}
          onAction={() => onActivate(plan.id, [])}
          data-test-role="plan-activate-no-selectors"
        >
          {t("page.taskPlans.rowActions.activateNoSelectors")}
        </MenuListItem>
      </Menu>
    </div>
  );
}

function PlanRow({
  plan,
  selected,
  select,
  t,
  onActivate,
  isActivating,
  onResetActivation,
  isResetting,
}: {
  plan: TaskPlan;
  selected: boolean;
  select: () => void;
  t: TFunction;
  onActivate: (planId: string, selectorIds?: string[]) => void;
  isActivating: boolean;
  onResetActivation: (planId: string) => void;
  isResetting: boolean;
}) {
  const { org, location, lang } = Route.useParams();

  const routeList = plan.routes.map((route) => {
    const routeKey = `${plan.id}-${route.route_code}`;

    return (
      <span
        data-test-role="plan-row-route-code"
        key={routeKey}
        className={styles.taskPlanRouteItem}
      >
        {route.route_code}
      </span>
    );
  });

  const orphanedTasks = countTasksWithOrphans(plan);

  return (
    <TableRow key={plan.id} data-test-role="task-plan-row">
      <TableCell>
        <div className={styles.taskPlanRowCheckboxContainer}>
          <Checkbox
            isSelected={selected}
            onChange={select}
            id={plan.id}
            name={plan.id}
            value={plan.id}
          />
        </div>
      </TableCell>
      <TableCell>
        {/*
          Named from `internal_id`, the warehouse-scoped number, with the ULID on
          the `title`. The route stays keyed on the ULID (and so does the checkbox
          above, which feeds the activate call) - the change is what a human is
          asked to read, not what we address the plan by.
        */}
        <Link
          to="/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan"
          params={{ "task-plan": plan.id, org, location, lang }}
          className={sharedStyles.truncatedId}
          title={plan.id}
        >
          {taskPlanName(plan.internal_id)}
        </Link>
      </TableCell>
      <TableCell>
        <PlanStatusCell plan={plan} t={t} />
      </TableCell>
      <TableCell>
        <div className={styles.taskPlanRouteListCellContainer}>{routeList}</div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-4" data-test-role="plan-row-zones">
          {zoneLabelsOf(plan.zones).map((label) => (
            <Tag key={label} label={label} display={{ tone: "info" }} />
          ))}
        </div>
      </TableCell>
      <TableCell>
        <span data-test-role="plan-row-customers">
          {customerNamesOf(plan.customers ?? []).join(", ")}
        </span>
      </TableCell>
      <TableCell>
        <div className={sharedStyles.metricCellContainer}>
          <span className={sharedStyles.metricCellMainValue}>
            {plan.total_tasks}
          </span>
          {orphanedTasks > 0 ? (
            <span
              className={sharedStyles.metricCellAuxValue}
              data-test-role="plan-row-orphan-count"
            >
              ({orphanedTasks})
            </span>
          ) : (
            <></>
          )}
        </div>
      </TableCell>
      <TableCell>
        <DateValue dateValue={plan.created_at} />
      </TableCell>
      <TableCell>
        <PlanActionsCell
          plan={plan}
          t={t}
          onActivate={onActivate}
          isActivating={isActivating}
          onResetActivation={onResetActivation}
          isResetting={isResetting}
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * The activation filter control.
 *
 * Lives in the table's action row rather than above it so it sits with the other
 * things that change what the table shows. That means it is only mounted in the
 * states that render the table -- loaded, empty and failed -- which is the same
 * bargain the pager already makes, and it is the empty state that matters: a
 * filter that hides its own rows has to be reachable to be undone.
 */
type ActivationItem = ss.Item; // { id: ActivationFilter, label }

/** Pure. Nothing selected = unfiltered; the placeholder says "All". */
function activationSelectState(a: {
  activation: ActivationFilter | undefined;
  t: TFunction;
}): SelectState<ActivationItem> {
  const activated: ActivationItem = {
    id: ActivationFilter.ACTIVATED,
    label: a.t("page.taskPlans.activationFilter.activated"),
  };
  const notActivated: ActivationItem = {
    id: ActivationFilter.NOT_ACTIVATED,
    label: a.t("page.taskPlans.activationFilter.notActivated"),
  };
  const base = ss.single<ActivationItem>({
    label: ss.ariaLabel(a.t("page.taskPlans.activationFilter.ariaLabel")),
    placeholder: a.t("page.taskPlans.activationFilter.all"),
    options: ss.fixed<ActivationItem>([activated, notActivated]),
  });
  return (
    match(a.activation)
      .with(ActivationFilter.ACTIVATED, () => ss.withPick(base, activated))
      .with(ActivationFilter.NOT_ACTIVATED, () =>
        ss.withPick(base, notActivated),
      )
      // `undefined`, or a member this screen does not offer: degrades to unfiltered
      .otherwise(() => base)
  );
}

function ActivationFilterSelect({
  activation,
  onChange,
  t,
}: {
  activation: ActivationFilter | undefined;
  onChange: (next: ActivationFilter | undefined) => void;
  t: TFunction;
}) {
  const onMsg = (msg: SelectMsg<ActivationItem>) =>
    match(msg)
      .with({ kind: "picked" }, ({ item }) =>
        onChange(validateActivationFilter(item.id)),
      )
      .with({ kind: "cleared" }, () => onChange(undefined)) // the `x`: back to unfiltered
      // `single` + fixed + no search: nothing else carries information
      .otherwise(() => {});

  return (
    <div
      className={styles.taskPlanActivationFilter}
      data-test-role="plan-activation-filter"
    >
      <Select
        state={activationSelectState({ activation, t })}
        onMsg={onMsg}
        display={{ trigger: { minWidth: "160px" } }}
      />
    </div>
  );
}

function TaskPlanTable({
  variants,
  selectedPlans,
  setState,
  children,
  pager,
  filter,
  t,
}: {
  variants?: Array<"orphans">;
  selectedPlans?: Array<TaskPlan>;
  setState: (state: State) => void;
  children?: React.ReactNode;
  pager?: React.ReactNode;
  filter?: React.ReactNode;
  t: TFunction;
}) {
  const taskCellTitle = (variants ?? []).includes("orphans")
    ? t("page.taskPlans.table.tasksWithOrphans")
    : t("page.taskPlans.table.tasks");

  const hasOptimizablePlans =
    (selectedPlans ?? []).filter((plan) => countTasksWithOrphans(plan) > 0)
      .length > 1;

  const { org, location, lang } = Route.useParams();

  return (
    <div>
      <div
        data-test-role="task-plan-actions"
        className={styles.taskPlanActionContainer}
      >
        {/* The pager shares this row rather than adding one of its own. */}
        <div className={styles.taskPlanActionContainerLeft}>{pager}</div>
        <div className={styles.taskPlanActionContainerRight}>
          {filter}
          <Menu
            trigger={{
              kind: "text-icon-left",
              variant: "tertiary",
              icon: "Menu",
              text: t("page.taskPlans.actions"),
            }}
          >
            <MenuListItem
              onAction={() => setState({ kind: "refresh-started" })}
            >
              <div className={styles.taskPlanActionMenuItem}>
                <div className={styles.taskPlanActionMenuItemIcon}>
                  <RefreshCw size={20} />
                </div>
                <div className={styles.taskPlanActionMenuItemLabel}>
                  <span>{t("page.taskPlans.tableActions.refreshPlans")}</span>
                </div>
              </div>
            </MenuListItem>

            <MenuListItem
              isDisabled={!hasOptimizablePlans}
              onAction={() =>
                setState({ kind: "optimizing", targets: selectedPlans ?? [] })
              }
            >
              <div className={styles.taskPlanActionMenuItem}>
                <div className={styles.taskPlanActionMenuItemIcon}>
                  <GitMerge size={20} />
                </div>
                <div className={styles.taskPlanActionMenuItemLabel}>
                  <span>{t("page.taskPlans.tableActions.mergePlans")}</span>
                </div>
              </div>
            </MenuListItem>
          </Menu>
          <div className={styles.taskPlanActionContainerRight}>
            <Link
              to="/$lang/orgs/$org/internal/whs/$location/task-plan-errors"
              params={{ org, location, lang }}
              className={sharedStyles.truncatedId}
            >
              {t("page.taskPlans.taskPlanErrors")}
            </Link>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table
          aria-label={t("page.taskPlans.table.ariaLabel")}
          isResizable={false}
        >
          <TableHead>
            <TableHeaderCell></TableHeaderCell>
            <TableHeaderCell isRowHeader={true}>
              {t("page.taskPlans.table.plan")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.taskPlans.table.status")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.taskPlans.table.routes")}
            </TableHeaderCell>
            <TableHeaderCell>{t("page.taskPlans.table.zones")}</TableHeaderCell>
            <TableHeaderCell>
              {t("page.taskPlans.table.customers")}
            </TableHeaderCell>
            <TableHeaderCell>{taskCellTitle}</TableHeaderCell>
            <TableHeaderCell>
              {t("page.taskPlans.table.created")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.taskPlans.table.actions")}
            </TableHeaderCell>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </div>
    </div>
  );
}

function assertNever(x: never): never {
  throw new Error(`Unhandled case: ${JSON.stringify(x)}`);
}

/**
 * The per-row mutations, bundled so `renderState` stays readable as they
 * accumulate. The `*ingId` fields name the single row a mutation is in flight
 * for, so only that row's control disables rather than the whole table's.
 */
type RowActions = {
  onActivate: (planId: string, selectorIds?: string[]) => void;
  activatingId: string | undefined;
  onResetActivation: (planId: string) => void;
  resettingId: string | undefined;
};

function renderState(
  setState: (state: State) => void,
  state: State,
  t: TFunction,
  actions: RowActions,
  pager: React.ReactNode,
  filter: React.ReactNode,
) {
  logger.trace(`Rendering task-plans route state '${state.kind}'`);

  switch (state.kind) {
    case "not-loaded":
    case "optimizing":
    case "loading":
    case "refresh-started":
      return (
        <div
          data-test-role="task-plans-loading"
          className={sharedStyles.loadingContainer}
        >
          <Loader type="page" />
        </div>
      );
    case "empty":
      return (
        <TaskPlanTable t={t} setState={setState} pager={pager} filter={filter}>
          <TableRow className={styles.emptyRow}>
            <TableCell colSpan={9}>
              <EmptyState heading={t("page.taskPlans.empty")} />
            </TableCell>
          </TableRow>
        </TaskPlanTable>
      );
    case "loaded": {
      const { selectedPlans = [] } = state;
      const rows = state.plans.map((plan) => {
        const selected = selectedPlans?.find((other) => plan.id === other.id);

        const select = () => {
          const next = selected
            ? selectedPlans.filter((other) => other.id !== plan.id)
            : [...selectedPlans, plan];

          setState({ ...state, selectedPlans: next });
        };

        return (
          <PlanRow
            key={plan.id}
            plan={plan}
            selected={isDefined(selected)}
            select={select}
            t={t}
            onActivate={actions.onActivate}
            isActivating={actions.activatingId === plan.id}
            onResetActivation={actions.onResetActivation}
            isResetting={actions.resettingId === plan.id}
          />
        );
      });

      const headerVariants: Array<"orphans"> = [];
      if (
        state.plans.filter((plan) => countTasksWithOrphans(plan) > 0).length > 0
      ) {
        headerVariants.push("orphans");
      }

      return (
        <TaskPlanTable
          t={t}
          setState={setState}
          selectedPlans={state.selectedPlans}
          variants={headerVariants}
          pager={pager}
          filter={filter}
        >
          {rows}
        </TaskPlanTable>
      );
    }
    case "failed":
      return (
        <TaskPlanTable t={t} setState={setState} pager={pager} filter={filter}>
          <TableRow>
            <TableCell colSpan={9}>
              <div className={sharedStyles.errorContainer}>
                <AppError
                  description={t("page.taskPlans.failed")}
                  resolution={t("page.taskPlans.failureSupport")}
                />
              </div>
            </TableCell>
          </TableRow>
        </TaskPlanTable>
      );
    default:
      return assertNever(state);
  }
}

function nextState<T>(
  state: State,
  query: UseQueryResult<TaskPlanListResponse, T>,
): State | undefined {
  switch (state.kind) {
    case "not-loaded":
      if (query.isFetching) {
        return { kind: "loading" };
      }
      break;
    case "loading": {
      logger.info("Checking query state for state update", {
        isRefetching: query.isRefetching,
        isSuccess: query.isSuccess,
        isPending: query.isPending,
        isLoading: query.isLoading,
      });
      if (query.isSuccess && !query.isPending && !query.isLoading) {
        const plans = query.data.task_plans;

        if (plans.length === 0) {
          return { kind: "empty" };
        }

        return {
          kind: "loaded",
          plans,
          selectedPlans: [],
          dataUpdatedAt: query.dataUpdatedAt,
        };
      }
      if (query.isError) {
        return { kind: "failed" };
      }
      break;
    }
    case "loaded": {
      if (query.isSuccess && query.dataUpdatedAt !== state.dataUpdatedAt) {
        const plans = query.data.task_plans;
        if (plans.length === 0) {
          return { kind: "empty" };
        }
        const stillThere = new Set(plans.map((p) => p.id));
        return {
          kind: "loaded",
          plans,
          selectedPlans: state.selectedPlans.filter((p) =>
            stillThere.has(p.id),
          ),
          dataUpdatedAt: query.dataUpdatedAt,
        };
      }
      break;
    }
  }

  return void 0;
}

export function RouteComponent() {
  const { lang } = Route.useParams();
  const { activation, ...page } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const [state, setState] = useState<State>({ kind: "not-loaded" });

  const client = useTaskPlannerApi();

  const { orgId, warehouseId } = useAssertedTenancyContext();
  const tenantId = AuthUtils.getTenantId(authState, orgId, warehouseId);

  const query = client.useQuery("get", "/{orgId}/task-plans", {
    params: {
      path: { orgId },
      query: {
        warehouseExtId: warehouseId,
        limit: PAGE_SIZE,
        ...activationQuery(activation),
        ...pageQuery(page),
      },
    },
  });

  const mutation = client.useMutation(
    "post",
    "/{orgId}/task-plans/optimize-ppt",
  );

  const activateMutation = client.useMutation(
    "post",
    "/{orgId}/task-plans/{id}/activate",
  );

  const resetMutation = client.useMutation(
    "post",
    "/{orgId}/task-plans/{id}/reset-activation",
  );

  const onActivate = (planId: string, selectorIds?: string[]) => {
    if (activateMutation.isPending) {
      logger.info("Avoiding double activation");
      return;
    }

    logger.info("Activating task plan", { id: planId, selectorIds });

    activateMutation
      .mutateAsync({
        params: { path: { orgId, id: planId } },
        // The body is required. No array: the full seeded warehouse roster.
        // `[]`: nobody, every task pooled for dispatch. Non-empty: exactly
        // those selectors.
        body:
          selectorIds === undefined
            ? { kind: "round_robin_all" }
            : selectorIds.length === 0
              ? { kind: "no_assignment" }
              : { kind: "round_robin_first", selector_ids: selectorIds },
      })
      .then(() => {
        logger.info("Task plan activation accepted", { id: planId });
        toast.success(t("page.taskPlans.activate.success"));
        query.refetch();
      })
      .catch((error) => {
        logger.warn("Task plan activation failed", { error });
        toast.error(t("page.taskPlans.activate.error"));
      });
  };

  const activatingId = activateMutation.isPending
    ? activateMutation.variables?.params?.path?.id
    : undefined;

  const onResetActivation = (planId: string) => {
    if (resetMutation.isPending) {
      logger.info("Avoiding double activation reset");
      return;
    }

    logger.info("Resetting plan activation state", { id: planId });

    resetMutation
      .mutateAsync({ params: { path: { orgId, id: planId } } })
      .then(() => {
        logger.info("Plan activation state reset", { id: planId });
        toast.success(t("page.taskPlans.resetActivation.success"));
        query.refetch();
      })
      .catch((error: unknown) => {
        logger.warn("Plan activation reset failed", { id: planId, error });
        // openapi-fetch rejects with the parsed ErrorResponse envelope rather
        // than an Error, so the server's own wording is what we surface. The
        // likely case is a 409 -- an activation went in-flight since this page
        // last loaded, which the disabled menu item could not have known.
        toast.error(
          errorMessage(error) ?? t("page.taskPlans.resetActivation.error"),
        );
        query.refetch();
      });
  };

  const resettingId = resetMutation.isPending
    ? resetMutation.variables?.params?.path?.id
    : undefined;

  const onActivationChange = (next: ActivationFilter | undefined) => {
    if (next === activation) {
      return;
    }

    // Resets the state machine the way paging does, and drops the cursor and
    // its direction with it: a cursor names a boundary in the result set that
    // produced it, so it addresses nothing once the filter changes. Replacing
    // the search wholesale rather than spreading `prev` is what drops them --
    // filtering returns to page 1.
    setState({ kind: "not-loaded" });
    navigate({ search: () => (isDefined(next) ? { activation: next } : {}) });
  };

  useEffect(() => {
    if (state.kind === "refresh-started") {
      setState({ kind: "not-loaded" });
      query.refetch();
      return;
    }

    if (state.kind === "optimizing") {
      if (mutation.isPending) {
        logger.info("Avoiding double mutation");
        return;
      }

      logger.info("Optimizing task plans", {
        ids: state.targets.map((plan) => plan.id),
      });

      const targetIds = state.targets.map((plan) => plan.id);

      setState({ kind: "not-loaded" });

      mutation
        .mutateAsync({
          params: { path: { orgId } },
          body: { origin_task_plan_ids: targetIds },
        })
        .then(() => {
          logger.info("Optimize PPT job request completed successfully");
          query.refetch();
        })
        .catch((error) => {
          logger.warn("Optimize PPT job request failed with error", { error });
          query.refetch();
        });

      return;
    }

    const updatedState = nextState(state, query);

    if (typeof updatedState !== "undefined") {
      setState(updatedState);
    }
  }, [query, state, setState, mutation, orgId]);

  return (
    <div
      className="bg-surface h-full w-full"
      data-tenant-id={tenantId}
      data-org-id={orgId}
    >
      {renderState(
        setState,
        state,
        t,
        { onActivate, activatingId, onResetActivation, resettingId },
        <ListPager
          pagination={query.data?.pagination}
          isFetching={query.isFetching}
          onChange={(next) => {
            // Paging resets the selection: a merge is built from checkboxes,
            // and rows that are no longer on screen cannot be reviewed before
            // it is fired. Backward paging resets it for the same reason --
            // the rows leaving the screen are the ones that were ticked.
            setState({ kind: "not-loaded" });
            navigate({
              search: (prev) => ({
                ...prev,
                cursor: next.cursor,
                dir: next.dir,
              }),
            });
          }}
          t={t}
        />,
        <ActivationFilterSelect
          activation={activation}
          onChange={onActivationChange}
          t={t}
        />,
      )}
    </div>
  );
}
